import { ORPCError } from "@orpc/client";
import {
  environmentManager,
  MutationCache,
  QueryClient,
} from "@tanstack/react-query";

/*
  The queryClient must be stable in the client, but when using SSR, it must be
  recreated for each request on the server, so usually there are three
  implementations routes:

  1. Full SPA, no-SSR:

  Define it outside of React. This becomes dangerous when enabling SSR as it
  can leak details between clients.

  ```tsx
  export const queryClient = new QueryClient();
  function App() {
    return <QueryProvider queryClient={queryClient}>{...}</QueryProvider>;
  }
  ```

  2. Defensive programming in case SSR gets enabled:

  Define it in a `useState` at the root of the app. This solves the issue with
  SSR (as every component is only ever rendered once on the server) and still
  makes it stable on the client.
  However, the instance can no longer be accessed outside of React (eg. in a `clientLoader`).

  ```tsx
  function App() {
    const [queryClient] = useState(() => new QueryClient());

    return <QueryProvider queryClient={queryClient}>{...}</QueryProvider>;
  }
  ```

  3. Best of both worlds:

  Create a new instance each time it's required on the server but maintain a
  single singleton instance in the browser.
  This makes the instance globally available in the client, while solving the
  security issue of the first solution.

  This is the approach we're taking here.
*/

const MAX_QUERY_RETRIES = 3;

// The client errors a later attempt can turn into a success: a 401 can follow
// an access token that expired mid-request, and the next attempt asks Auth0 for
// a fresh one.
const RETRYABLE_CLIENT_ERROR_STATUSES = new Set([401, 408, 429]);

/**
 * TanStack Query retries every failure three times, about seven seconds in all.
 * A refusal or a missing record answers the same way on every attempt, so for
 * those the retries only keep a spinner on screen before the error shows.
 */
export function shouldRetryQuery(failureCount: number, error: Error) {
  const isClientError =
    error instanceof ORPCError && error.status >= 400 && error.status < 500;
  const isDeterministicFailure =
    isClientError && !RETRYABLE_CLIENT_ERROR_STATUSES.has(error.status);

  if (isDeterministicFailure) {
    return false;
  }

  return failureCount < MAX_QUERY_RETRIES;
}

function makeQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { retry: shouldRetryQuery },
    },
    mutationCache: new MutationCache({
      onSuccess: () => {
        void queryClient.invalidateQueries();
      },
    }),
  });

  return queryClient;
}

let browserQueryClient: QueryClient | undefined = undefined;

export function getQueryClient() {
  if (environmentManager.isServer()) {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }

    return browserQueryClient;
  }
}
